import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { ensureCsrf, createOrder, initTbankPayment } from '../../api/orders';
import { getPublicStoreSettings } from '../../api/storeSettings';
import './Cart.css';

const DRAFT_KEY = 'lavsan_draft_order';

export default function CartPage() {
  const { items, inc, dec, remove, clear, totalSum } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState('delivery');
  const [agreePD, setAgreePD] = useState(false);
  const [agreeOferta, setAgreeOferta] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [comment, setComment] = useState('');
  const [draftOrder, setDraftOrder] = useState(null);
  const [settings, setSettings] = useState(null);

  const [buyer, setBuyer] = useState({
    name: '',
    phone: '',
    address: '',
    email: '',
  });

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (raw) setDraftOrder(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const s = await getPublicStoreSettings();
        if (!ok) return;
        setSettings(s);
        if (s.deliveryEnabled === false && s.pickupEnabled) setMethod('pickup');
        if (s.pickupEnabled === false && s.deliveryEnabled) setMethod('delivery');
      } catch {
        if (ok) setSettings({
          deliveryEnabled: true,
          pickupEnabled: true,
          deliveryPrice: 0,
          freeDeliveryFrom: null,
          pickupHint: 'Самовывоз со склада. Адрес и график указаны ниже; время выдачи можно уточнить у менеджера.',
          pickupAddress: '',
          deliveryHint: '',
        });
      }
    })();
    return () => { ok = false; };
  }, []);

  useEffect(() => {
    if (!user) return;
    setBuyer((prev) => ({
      name: prev.name || user.name || '',
      phone: prev.phone || user.phone || '',
      address: prev.address || '',
      email: prev.email || user.email || '',
    }));
    if (user.marketingConsent) setAgreeMarketing(true);
  }, [user]);

  const deliveryPreview = useMemo(() => {
    if (!settings || method !== 'delivery') return 0;
    const price = Number(settings.deliveryPrice || 0);
    const freeFrom = Number(settings.freeDeliveryFrom);
    if (freeFrom > 0 && totalSum >= freeFrom) return 0;
    return price;
  }, [settings, method, totalSum]);

  const previewPay = useMemo(() => {
    return Math.max(0, totalSum + deliveryPreview);
  }, [totalSum, deliveryPreview]);

  function setBuyerField(field) {
    return (e) => setBuyer((p) => ({ ...p, [field]: e.target.value }));
  }

  function persistDraft(order) {
    setDraftOrder(order);
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(order));
    } catch { /* ignore */ }
  }

  function clearDraft() {
    setDraftOrder(null);
    try {
      sessionStorage.removeItem(DRAFT_KEY);
    } catch { /* ignore */ }
  }

  async function handleCreateOrder() {
    if (!user) {
      navigate('/auth');
      return;
    }

    const name = buyer.name.trim();
    const phone = buyer.phone.trim();
    const address = buyer.address.trim();

    if (!name || !phone) {
      alert('Укажите имя и телефон для оформления заказа');
      return;
    }
    if (method === 'delivery' && !address) {
      alert('Укажите адрес доставки');
      return;
    }
    if (!agreePD || !agreeOferta) {
      alert('Нужно согласие на обработку персональных данных и принятие публичной оферты');
      return;
    }

    try {
      setSubmitting(true);
      await ensureCsrf();

      const order = await createOrder(
        items.map((item) => ({ productId: item.id, qty: item.qty })),
        {
          name,
          phone,
          address: method === 'pickup' ? (address || settings?.pickupAddress || 'Самовывоз') : address,
          email: buyer.email.trim(),
        },
        {
          agreeOferta: true,
          agreeMarketing,
          bonusRedeem: 0,
          fulfillment: {
            method,
            comment: comment.trim(),
          },
        }
      );

      // заказ уже в резерве — корзину очищаем, чтобы не задвоить
      clear();
      persistDraft(order);
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Ошибка оформления заказа';

      if (status === 401) {
        navigate('/auth');
        return;
      }

      alert(msg);
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePay({ force = false } = {}) {
    if (!draftOrder?.orderId) return;
    try {
      setSubmitting(true);
      await ensureCsrf();
      const pay = await initTbankPayment(draftOrder.orderId, { force });
      if (!pay?.paymentUrl) throw new Error('Не удалось получить ссылку на оплату');
      window.location.href = pay.paymentUrl;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Ошибка оплаты';
      // если старая ссылка мертва — пробуем новую один раз
      if (!force && /expired|истёк|неуда|ошибк/i.test(String(msg))) {
        return handlePay({ force: true });
      }
      const retry = window.confirm(`${msg}\n\nПолучить новую ссылку на оплату?`);
      if (retry) return handlePay({ force: true });
    } finally {
      setSubmitting(false);
    }
  }

  if (!items.length && !draftOrder) {
    return (
      <div className="cart-page container">
        <h1>Корзина</h1>
        <div className="cart-empty">
          Пока пусто.
          <br />
          <Link to="/catalog">Перейти в каталог</Link>
        </div>
      </div>
    );
  }

  if (draftOrder) {
    const until = draftOrder.reserveExpiresAt
      ? new Date(draftOrder.reserveExpiresAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      : '';
    return (
      <div className="cart-page container">
        <h1>Подтверждение заказа</h1>
        <section className="cart-checkout cart-confirm">
          <p>Заказ <b>№{draftOrder.number}</b> создан и зарезервирован{until ? ` до ${until}` : ''}.</p>
          <p>Способ получения: <b>{draftOrder.fulfillment?.method === 'pickup' ? 'Самовывоз' : 'Доставка'}</b></p>
          <p>Сумма товаров: <b>{Number(draftOrder.subtotal || 0).toLocaleString('ru-RU')} ₽</b></p>
          {Number(draftOrder.deliveryPrice || 0) > 0 && (
            <p>Доставка: <b>{Number(draftOrder.deliveryPrice).toLocaleString('ru-RU')} ₽</b></p>
          )}
          <p className="cart-sum">К оплате: <b>{Number(draftOrder.total || 0).toLocaleString('ru-RU')} ₽</b></p>
          <p className="hint">
            Корзина очищена. Отменить заказ может только менеджер — напишите нам или зайдите в «Мои заказы».
          </p>
          <div className="cart-footer">
            <Link className="btn-outline" to="/account/orders" onClick={clearDraft}>
              Мои заказы
            </Link>
            <button className="btn-primary" onClick={() => handlePay()} disabled={submitting}>
              {submitting ? 'Переходим…' : 'Оплатить картой'}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const deliveryOn = settings?.deliveryEnabled !== false;
  const pickupOn = settings?.pickupEnabled !== false;

  return (
    <div className="cart-page container">
      <h1>Корзина</h1>

      <div className="cart-layout">
        <div className="cart-table">
          {items.map((item) => (
            <div key={item.id} className="cart-row">
              <Link to={item.slug ? `/p/${item.slug}` : '#'} className="cart-thumb">
                {item.img ? <img src={item.img} alt={item.title || 'Товар'} /> : <div className="noimg" />}
              </Link>

              <div className="cart-main">
                <Link to={item.slug ? `/p/${item.slug}` : '#'} className="cart-title">
                  {item.title || 'Товар'}
                </Link>
              </div>

              <div className="cart-qty">
                <div className="qty-stepper" role="group" aria-label="Количество">
                  <button type="button" className="qty-btn" onClick={() => dec(item.id)} aria-label="Уменьшить">−</button>
                  <output className="qty-value" aria-live="polite">{item.qty}</output>
                  <button type="button" className="qty-btn" onClick={() => inc(item.id)} aria-label="Увеличить">+</button>
                </div>
              </div>

              <div className="cart-price">
                {(item.price || 0).toLocaleString('ru-RU')} ₽
              </div>
              <div className="cart-total">
                {(item.qty * (item.price || 0)).toLocaleString('ru-RU')} ₽
              </div>
              <button className="cart-remove" onClick={() => remove(item.id)} title="Убрать">✕</button>
            </div>
          ))}
        </div>

        <section className="cart-checkout">
          <h2>Оформление</h2>

          <div className="cart-method">
            {deliveryOn && (
              <label className="radio">
                <input
                  type="radio"
                  name="fulfillment"
                  checked={method === 'delivery'}
                  onChange={() => setMethod('delivery')}
                />
                <span>Доставка{deliveryPreview > 0 ? ` (${deliveryPreview} ₽)` : deliveryPreview === 0 && Number(settings?.deliveryPrice) > 0 ? ' (бесплатно)' : ''}</span>
              </label>
            )}
            {pickupOn && (
              <label className="radio">
                <input
                  type="radio"
                  name="fulfillment"
                  checked={method === 'pickup'}
                  onChange={() => setMethod('pickup')}
                />
                <span>Самовывоз</span>
              </label>
            )}
          </div>
          {method === 'pickup' && (
            <p className="hint">
              {settings?.pickupHint || ''}
              {settings?.pickupAddress ? ` Адрес: ${settings.pickupAddress}` : ''}
            </p>
          )}
          {method === 'delivery' && settings?.deliveryHint && (
            <p className="hint">{settings.deliveryHint}</p>
          )}

          <p className="cart-bonus-off">Бонусная программа пока недоступна</p>

          <div className="cart-checkout__grid">
            <label>
              Имя *
              <input value={buyer.name} onChange={setBuyerField('name')} autoComplete="name" required />
            </label>
            <label>
              Телефон *
              <input value={buyer.phone} onChange={setBuyerField('phone')} autoComplete="tel" required />
            </label>
            {method === 'delivery' && (
              <label className="cart-checkout__full">
                Адрес доставки *
                <input value={buyer.address} onChange={setBuyerField('address')} autoComplete="street-address" required />
              </label>
            )}
            <label className="cart-checkout__full">
              Email (необязательно)
              <input type="email" value={buyer.email} onChange={setBuyerField('email')} autoComplete="email" />
            </label>
            <label className="cart-checkout__full">
              Комментарий
              <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Подъезд, время, пожелания" />
            </label>
          </div>

          <label className="cart-agree">
            <input type="checkbox" checked={agreePD} onChange={(e) => setAgreePD(e.target.checked)} />
            <span>
              Даю согласие на обработку персональных данных в соответствии с{' '}
              <Link to="/privacy" target="_blank">политикой конфиденциальности</Link>
            </span>
          </label>

          <label className="cart-agree">
            <input type="checkbox" checked={agreeOferta} onChange={(e) => setAgreeOferta(e.target.checked)} />
            <span>
              Принимаю условия{' '}
              <Link to="/oferta" target="_blank">публичной оферты</Link>
              . С порядком возврата ознакомлен (
              <Link to="/return-memo" target="_blank">памятка</Link>).
            </span>
          </label>

          <label className="cart-agree">
            <input type="checkbox" checked={agreeMarketing} onChange={(e) => setAgreeMarketing(e.target.checked)} />
            <span>
              Согласен получать рекламные рассылки о товарах и акциях (необязательно).{' '}
              <Link to="/privacy#marketing" target="_blank">Подробнее</Link>
            </span>
          </label>

          <div className="cart-footer">
            <div className="cart-sum">
              <div>В корзине: <b>{totalSum.toLocaleString('ru-RU')} ₽</b></div>
              {deliveryPreview > 0 ? (
                <div className="hint">К оплате ≈ {previewPay.toLocaleString('ru-RU')} ₽</div>
              ) : null}
            </div>
            <button className="btn-primary" onClick={handleCreateOrder} disabled={submitting || !agreePD || !agreeOferta}>
              {submitting ? 'Оформляем…' : 'Оформить заказ'}
            </button>
            <button className="btn-outline" onClick={clear} disabled={submitting}>Очистить</button>
          </div>
        </section>
      </div>
    </div>
  );
}
